import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { translate } from 'i18n/i18n'
import moment from 'moment-timezone'
import { useMemo } from 'react'
import type { TProperty } from 'stores/fieldAssetStore/helperTypes'
import { checkAuthorization } from 'utils/checkAuthorization'
import { searchStringFromStringWithSpaces } from 'utils/searchStringFromStringWithSpaces'

export const useFilteredData = ({
  files,
  filterActiveForPermitNumbers,
  filterActiveForProperties,
  filterActiveForTypes,
  filterActiveForYears,
  filteredPermitNumbers,
  filteredProperties,
  filteredTypes,
  filteredYears,
  properties,
  searchString,
}: {
  files: routes.PheromonePurRecDownload.Response['files']
  filterActiveForPermitNumbers: boolean
  filterActiveForProperties: boolean
  filterActiveForTypes: boolean
  filterActiveForYears: boolean
  filteredPermitNumbers: { [permitNumber: string]: boolean }
  filteredProperties: { [propertyId: string]: boolean }
  filteredTypes: { [type: string]: boolean }
  filteredYears: { [year: string]: boolean }
  properties: TProperty[]
  searchString: string
}) => {
  return useMemo(() => {
    return files.filter((f) => {
      return [
        {
          /* TYPES */
          isActive: filterActiveForTypes,
          check: () => filteredTypes[f.type],
        },
        {
          /* PROPERTIES */
          isActive: filterActiveForProperties,
          check: () =>
            f.propertyIds.some((propertyId: number) => {
              const property = properties.find((p: TProperty) => p.propertyId === propertyId)

              return property && filteredProperties[propertyId]
            }),
        },
        {
          /* PermitNumbers */
          isActive: filterActiveForPermitNumbers,
          check: () => filteredPermitNumbers[f.permitNumber],
        },
        {
          /* Years */
          isActive: filterActiveForYears,
          check: () => {
            const year =
              f.type === 'REC' && 'year' in f
                ? String(f.year)
                : String(moment.tz(f.dateAppliedYYYYMMDD, moment.tz.guess()).year())

            return filteredYears[year]
          },
        },
        {
          isActive: !!searchString,
          check: () =>
            searchStringFromStringWithSpaces(
              searchString,
              `${f.propertyIds
                .map((propertyId) => {
                  const property = properties.find((p: TProperty) => p.propertyId === propertyId)

                  return checkAuthorization({ entity: propertyId, permission: 'EXPORT_PHEROMONE_PUR_REC' }) &&
                    property
                    ? property.propertyName
                    : ''
                })
                .join(' ')} ${f.permitNumber} ${f.type} ${
                'year' in f ? f.year : translate.phrases.abbreviations('Not Applicable')
              }`,
            ),
        },
      ].every((filterItem) => !filterItem.isActive || filterItem.check())
    })
  }, [
    files,
    filterActiveForPermitNumbers,
    filterActiveForProperties,
    filterActiveForTypes,
    filterActiveForYears,
    filteredPermitNumbers,
    filteredProperties,
    filteredTypes,
    filteredYears,
    properties,
    searchString,
  ])
}
